// lib/fieldsync.ts — the crew's offline outbox (lib/outbox.ts), wired to the
// app's store (app.tsx useStore) and to Crew Mode / the job screens.
//
//   store.fieldOp({ kind, jobId, payload })   apply to this phone's copy of the
//                                             job now, queue for the service
//   useOutbox()                               what's waiting, for the indicator
//   fieldView(state)                          the jobs with every op still on its
//                                             way applied (a fresh copy from the
//                                             cloud never hides them)
//
// Only a signed-in device with the service configured queues anything. Local
// mode and the sandbox apply the op to their own copy and stop there — the
// same place their saves have always stopped.
import React from "react";
import {
  createOutbox, pickStore, applyFieldOp, stampFieldOp, revertFieldOp, overlayOps, mapJob, opOf, makeKey, opLabel,
  SYNC_TAG, type Outbox, type OutboxView, type FieldKind, type OutboxEntry, type OutboxStore,
} from "./outbox";

const W: any = typeof window !== "undefined" ? window : {};
let box: Outbox | null = null;
let boxStore: OutboxStore | null = null;
let view: OutboxView = { entries: [], recent: [], flushing: false, loaded: false, lastDrainAt: 0, storage: "" };
const subs = new Set<(v: OutboxView) => void>();
let ver = 0;

export function outboxConfigured(): boolean {
  const demo = !!(W.VFP_DEMO && W.VFP_DEMO.active);
  return !demo && !!W.VFP_API_BASE && !!(W.VFP_DB && W.VFP_DB.ready && W.VFP_DB.crewOp);
}
export const outboxReady = () => !!box;
const online = () => typeof navigator === "undefined" || navigator.onLine !== false;

function publish(v: OutboxView) { view = v; ver++; subs.forEach((fn) => { try { fn(v); } catch (e) { /* a listener never breaks the queue */ } }); }

/* Background Sync where the browser has it (Chrome/Edge/Android): the service
   worker sends the queue when the signal comes back even if the app is
   closed. It needs the api base and a current token, left in the outbox's
   meta store; elsewhere the app flushes on its next open. */
let authAt = 0;
/* only while online: the workspace lookup offline is a fallback, and the
   token/workspace written the last time there was signal are the ones to use */
async function saveSyncAuth() {
  try {
    const DB = W.VFP_DB;
    if (!online() || !boxStore || !boxStore.setMeta || !DB || !DB.outboxAuth) return;
    const a = await DB.outboxAuth();
    if (a) { await boxStore.setMeta("auth", a); authAt = Date.now(); }
  } catch (e) { /* the next open flushes instead */ }
}
async function armBackgroundSync() {
  try {
    await saveSyncAuth();
    const sw: any = typeof navigator !== "undefined" && (navigator as any).serviceWorker;
    if (!sw || !sw.ready) return;
    const reg = await Promise.race([sw.ready, new Promise((r) => setTimeout(() => r(null), 1500))]) as any;
    if (reg && reg.sync && reg.sync.register) await reg.sync.register(SYNC_TAG);
  } catch (e) { /* not supported / not allowed: the next open flushes */ }
}

/* ---- the GPS fence as the service checks it ----
   A crew login's copy of the workspace has neither the clock-in settings nor
   the quotes (so no site pin from the parcel). The service hands the phone its
   real fence — radius, whether an override code is needed, the site pins for
   this login's open jobs — and the phone keeps it for when there's no signal,
   so a clock-in in the back yard is checked the way the service will check it. */
const POLICY_KEY = "fw_crew_policy_v1";
export type ServerPolicy = { on: boolean; radiusMi: number; codeRequired: boolean; sites: Record<string, [number, number]>; at: number };
let policy: ServerPolicy | null = (() => { try { const v = JSON.parse(localStorage.getItem(POLICY_KEY) || "null"); return v && typeof v === "object" && v.sites ? v : null; } catch (e) { return null; } })();
export const serverPolicy = () => policy;
let policyAt = 0;
export async function refreshPolicy(force = false) {
  const DB = W.VFP_DB;
  if (!outboxConfigured() || !DB || !DB.crewPolicy || !online()) return policy;
  if (!force && Date.now() - policyAt < 5 * 60 * 1000) return policy;
  policyAt = Date.now();
  try {
    const p = await DB.crewPolicy();
    if (p && typeof p === "object" && p.sites) {
      policy = { on: !!p.on, radiusMi: Number(p.radiusMi) || 0.1, codeRequired: !!p.codeRequired, sites: p.sites || {}, at: Number(p.at) || Date.now() };
      try { localStorage.setItem(POLICY_KEY, JSON.stringify(policy)); } catch (e) { /* a full device keeps the in-memory copy */ }
      publish(view);
    }
  } catch (e) { /* offline, older service: keep what we had */ }
  return policy;
}

export type FieldSyncDeps = {
  setState: (fn: (s: any) => any) => void;
  hydrated: () => boolean;
  save: () => void;
  notify: (msg: string, opts?: any) => void;
};

/* Start the outbox for this tab (once). Returns a stop function. */
export function startFieldSync(deps: FieldSyncDeps): () => void {
  if (box || !outboxConfigured()) return () => {};
  const DB = W.VFP_DB;
  let stopped = false;
  storeSet = deps.setState;
  const lockFn = (fn: () => Promise<void>) => {
    const locks: any = typeof navigator !== "undefined" && (navigator as any).locks;
    if (locks && locks.request) return locks.request("fw-outbox-flush", { ifAvailable: true }, async (l: any) => { if (l) await fn(); });
    return fn();
  };
  starting = true;
  const start = async () => {
    boxStore = await pickStore();
    if (stopped) { starting = false; return; }
    box = createOutbox({
      store: boxStore,
      send: (e: OutboxEntry) => DB.crewOp(e),
      isOnline: online,
      lock: lockFn,
      ws: () => (DB.ws ? DB.ws() : null),
      onAck: (e, outcome, body) => {
        const op = opOf(e);
        if (outcome === "held") {
          // the office decides: take it off this phone's copy of the job, say so
          deps.setState((s) => mapJob(s, e.jobId, (w) => revertFieldOp(w, op)));
          deps.notify(`${opLabel(e)} went to the office to review — ${body.message || "they'll approve it"}`, { kind: "info", ms: 9000 });
        } else if (body && body.stamp) {
          deps.setState((s) => mapJob(s, e.jobId, (w) => stampFieldOp(w, op, body.stamp)));
        }
      },
      onFail: (e) => deps.notify(`${opLabel(e)} couldn't be saved: ${e.last_error || "the service refused it"}. Open Crew Mode to discard or retry it.`, { kind: "error", ms: 10000 }),
      // a job's queue emptied: its row can go out with the next save
      onDrain: () => { if (deps.hydrated()) deps.save(); },
    });
    box.subscribe(publish);
    publish(box.view());
    try { DB.holdRows && DB.holdRows((table: string, id: string) => table === "work_orders" && !!box && box.pendingJobs().has(id)); } catch (e) { /* older bundle */ }
    starting = false;
    for (const x of early.splice(0)) x.resolve(await box.enqueue(x.entry).catch(() => null));
    await box.ready;
    if (online()) { box.flush(); saveSyncAuth(); }
    refreshPolicy(true);
  };
  start();
  const flush = () => { if (box && online()) { armBackgroundSync(); box.flush(); refreshPolicy(); } };
  const onVis = () => { if (typeof document !== "undefined" && document.visibilityState === "visible") flush(); };
  const onMsg = (ev: any) => { if (ev && ev.data && ev.data.type === "fw-outbox-flush") flush(); };
  // queued and online → send; otherwise keep Background Sync's token current
  // (access tokens last an hour) so a queue made later can go with the app closed
  const tick = setInterval(() => {
    if (!box) return;
    if (box.view().entries.some((e) => e.status === "queued")) flush();
    else if (Date.now() - authAt > 10 * 60 * 1000) saveSyncAuth();
  }, 60000);
  window.addEventListener("online", flush);
  window.addEventListener("focus", flush);
  document.addEventListener("visibilitychange", onVis);
  const sw: any = typeof navigator !== "undefined" && (navigator as any).serviceWorker;
  if (sw && sw.addEventListener) sw.addEventListener("message", onMsg);
  return () => {
    stopped = true;
    clearInterval(tick);
    window.removeEventListener("online", flush);
    window.removeEventListener("focus", flush);
    document.removeEventListener("visibilitychange", onVis);
    if (sw && sw.removeEventListener) sw.removeEventListener("message", onMsg);
    try { DB.holdRows && DB.holdRows(null); } catch (e) { /* ignore */ }
    if (box) box.stop();
    box = null;
    starting = false;
  };
}

/* A crew action: applied to this phone's copy of the job at once, then queued.
   `sent(ms)` resolves with the service's answer, or { outcome: "waiting" }
   when it hasn't gone yet (no signal) — the op is safe either way. */
export function runFieldOp(input0: { kind: FieldKind | string; jobId: string; payload?: any; at?: number; id?: string }, setState: (fn: (s: any) => any) => void) {
  const input = input0 as { kind: FieldKind; jobId: string; payload?: any; at?: number; id?: string };
  const id = input.id || makeKey();
  const at = Number(input.at) || Date.now();
  const payload = input.payload || {};
  const op = { id, kind: input.kind, at, payload };
  setState((s: any) => mapJob(s, input.jobId, (w) => applyFieldOp(w, op)));
  let queued: Promise<any> = Promise.resolve(null);
  const entry = { id, kind: input.kind, jobId: input.jobId, payload, created_at: at };
  if (box && outboxConfigured()) {
    queued = box.enqueue(entry).catch(() => null);
    if (!online()) armBackgroundSync();
  } else if (starting && outboxConfigured()) {
    // tapped while the outbox is still opening its storage (the first second
    // after a cold start): it goes on the queue the moment that's done
    queued = new Promise((resolve) => early.push({ entry, resolve }));
  }
  const willSend = !!(box || starting) && outboxConfigured();
  return {
    id,
    queued: willSend,
    sent: (ms = 8000): Promise<any> => (willSend ? queued.then(() => (box ? box.settled(id, ms) : { outcome: "waiting" })) : Promise.resolve({ outcome: "local" })),
  };
}
let starting = false;
const early: { entry: any; resolve: (v: any) => void }[] = [];

/* The crew's answer to a refused update. Discard takes it back off the job. */
export async function discardOp(id: string) {
  if (!box) return;
  const e = await box.discard(id);
  if (e && storeSet) storeSet((s: any) => mapJob(s, e.jobId, (w) => revertFieldOp(w, opOf(e))));
}
let storeSet: ((fn: (s: any) => any) => void) | null = null;
export function retryOp(id: string) { return box ? box.retry(id) : Promise.resolve(); }
export function flushNow() { if (box) box.flush(); }

/* the ops the phone's view carries: queued ones, and ones acked lately */
export function viewOps(v: OutboxView = view) {
  return [
    ...v.entries.filter((e) => e.status === "queued"),
    ...v.recent.filter((r) => r.result === "applied").map((r) => ({ id: r.id, kind: r.kind, jobId: r.jobId, created_at: r.created_at, payload: r.payload })),
  ];
}
export function fieldView(state: any) { return overlayOps(state, viewOps() as any); }

/* Re-render on outbox changes; returns the current view. */
export function useOutbox(): OutboxView {
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => { const fn = () => force(); subs.add(fn); return () => { subs.delete(fn); }; }, []);
  return view;
}
/* a number that moves whenever the outbox does (for effect deps) */
export function useOutboxVersion(): number { useOutbox(); return ver; }
